import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  type CreateOrganizationFormSchemaType,
  createOrganizationFormSchema,
} from './org-settings.schema'
import { generateSlugFromName } from './org-slug.util'

interface UseCreateOrganizationFormParams {
  shouldReset: boolean
}

export interface UseCreateOrganizationFormReturn {
  form: ReturnType<typeof useForm<CreateOrganizationFormSchemaType>>
  logoFile: File | null
  logoPreview: string | null
  handleNameChange: (value: string) => void
  handleSlugChange: (value: string) => void
  handleLogoChange: (file: File | null) => void
  handleLogoRemove: () => void
}

const useLogoState = (shouldReset: boolean) => {
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)

  useEffect(() => {
    if (shouldReset) {
      setLogoFile(null)
      setLogoPreview(null)
    }
  }, [shouldReset])

  const handleLogoChange = useCallback((file: File | null) => {
    setLogoFile(file)
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => setLogoPreview(reader.result as string)
      reader.readAsDataURL(file)
      return
    }
    setLogoPreview(null)
  }, [])

  const handleLogoRemove = useCallback(() => {
    setLogoFile(null)
    setLogoPreview(null)
  }, [])

  return { logoFile, logoPreview, handleLogoChange, handleLogoRemove }
}

export const useCreateOrganizationForm = ({
  shouldReset,
}: UseCreateOrganizationFormParams): UseCreateOrganizationFormReturn => {
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false)
  const logo = useLogoState(shouldReset)

  const form = useForm<CreateOrganizationFormSchemaType>({
    resolver: zodResolver(createOrganizationFormSchema),
    defaultValues: { name: '', slug: '' },
    mode: 'onChange',
  })

  useEffect(() => {
    if (shouldReset) {
      form.reset({ name: '', slug: '' })
      setIsSlugManuallyEdited(false)
    }
  }, [shouldReset, form])

  const handleNameChange = useCallback(
    (value: string) => {
      form.setValue('name', value, { shouldValidate: true })
      if (!isSlugManuallyEdited) {
        form.setValue('slug', generateSlugFromName(value), { shouldValidate: true })
      }
    },
    [form, isSlugManuallyEdited]
  )

  const handleSlugChange = useCallback(
    (value: string) => {
      setIsSlugManuallyEdited(true)
      form.setValue('slug', value.toLowerCase(), { shouldValidate: true })
    },
    [form]
  )

  return { form, handleNameChange, handleSlugChange, ...logo }
}
